import {Overlay} from 'platform/components';
import {Box, GridItem, Show} from 'platform/foundation';
import styled from 'styled-components';

import {type ReactNode, use, useState} from 'react';

import {CHART_GRID_COLUMNS} from '../../constants/CHART_GRID_COLUMNS';
import {ChartCardContext} from '../../contexts/ChartCardContext';
import {ChartGridContext} from '../../contexts/ChartGridContext';
import {ChartLayoutContext} from '../../contexts/ChartLayoutContext';
import {ChartCardToolbar} from '../ChartCardToolbar/ChartCardToolbar';

// An expanded card fills the height of its row, so two cards in a row end on the same line.
// A collapsed card keeps the height of its header.
const StyledCardFrame = styled.div<{$isFilling: boolean}>`
  position: relative;
  display: grid;
  height: ${({$isFilling}) => ($isFilling ? '100%' : 'auto')};
`;

// In edit mode, the overlay covers the card and only its toolbar takes input.
const StyledCardContent = styled.div<{$isInert: boolean}>`
  display: grid;
  pointer-events: ${({$isInert}) => ($isInert ? 'none' : 'auto')};
  user-select: ${({$isInert}) => ($isInert ? 'none' : 'auto')};
`;

interface ChartGridItemProps {
  /** Id of the card in its grid, for example `revenue`. */
  id: string;
  /** The chart card. Its `Card` takes the props of `useChartCardExpansion`. */
  children: ReactNode;
}

export function ChartGridItem(props: ChartGridItemProps) {
  const layoutId = use(ChartGridContext);
  const layout = use(ChartLayoutContext);
  const [isExpanded, setIsExpanded] = useState(true);

  const cardId = `${layoutId}.${props.id}`;
  const width = layout?.getWidth(cardId) ?? 'full';
  const isEditing = layout?.isEditing ?? false;

  return (
    <GridItem span={width === 'half' ? 1 : CHART_GRID_COLUMNS}>
      <ChartCardContext.Provider
        value={{width, isExpanded, toggleExpanded: () => setIsExpanded(!isExpanded)}}
      >
        <StyledCardFrame $isFilling={isExpanded}>
          <StyledCardContent inert={isEditing} $isInert={isEditing}>
            {props.children}
          </StyledCardContent>
          <Show when={isEditing ? layout : null}>
            {(editedLayout) => (
              <Overlay variant="neutral" zIndex="base">
                <Box position="absolute" top={2} right={2}>
                  <ChartCardToolbar
                    width={width}
                    onWidthChange={(nextWidth) => editedLayout.setWidth(cardId, nextWidth)}
                  />
                </Box>
              </Overlay>
            )}
          </Show>
        </StyledCardFrame>
      </ChartCardContext.Provider>
    </GridItem>
  );
}
